import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useArtworks, artworksDb, type Artwork } from '@/hooks/useArtworks';
import { useSubscribers } from '@/hooks/useSubscribers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

type Draft = Partial<Artwork> & { slug: string };

const emptyDraft: Draft = {
  slug: '',
  title_nl: '',
  title_en: '',
  description_nl: '',
  description_en: '',
  materials_nl: '',
  materials_en: '',
  dimensions: '',
  year: new Date().getFullYear(),
  price_cents: null,
  currency: 'EUR',
  sold: false,
  published: true,
  sort_order: 0,
  images: [],
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function AdminArtworks() {
  const { artworks, loading, reload } = useArtworks(true);
  const { subscribers, loading: subscribersLoading, reload: reloadSubscribers } = useSubscribers();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const edit = (a: Artwork) => {
    setDraft({ ...a });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded: { url: string }[] = [];
    for (const file of Array.from(files)) {
      const path = `${draft.slug || 'untitled'}/${Date.now()}-${slugify(file.name)}`;
      const { error } = await supabase.storage.from('artworks').upload(path, file, {
        upsert: true,
        contentType: file.type,
      });
      if (error) {
        toast.error(`Upload failed: ${error.message}`);
        continue;
      }
      const { data } = supabase.storage.from('artworks').getPublicUrl(path);
      uploaded.push({ url: data.publicUrl });
    }
    set({ images: [...(draft.images ?? []), ...uploaded] });
    setUploading(false);
  };

  const save = async () => {
    if (!draft.slug || !draft.title_nl) {
      toast.error('Slug and Dutch title are required');
      return;
    }
    setSaving(true);
    const payload = {
      slug: draft.slug,
      title_nl: draft.title_nl,
      title_en: draft.title_en || draft.title_nl,
      description_nl: draft.description_nl || null,
      description_en: draft.description_en || null,
      materials_nl: draft.materials_nl || null,
      materials_en: draft.materials_en || null,
      dimensions: draft.dimensions || null,
      year: draft.year ?? null,
      price_cents: draft.price_cents ?? null,
      currency: draft.currency || 'EUR',
      sold: !!draft.sold,
      published: draft.published !== false,
      sort_order: draft.sort_order ?? 0,
      images: draft.images ?? [],
    };
    const { error } = await artworksDb.from('artworks').upsert(payload, { onConflict: 'slug' });
    setSaving(false);
    if (error) toast.error(error.message);
    else {
      toast.success('Artwork saved');
      setDraft(emptyDraft);
      reload();
    }
  };

  const remove = async (a: Artwork) => {
    if (!confirm(`Delete "${a.title_nl}"?`)) return;
    const { error } = await artworksDb.from('artworks').delete().eq('id', a.id);
    if (error) toast.error(error.message);
    else {
      toast.success('Deleted');
      reload();
    }
  };

  const nlCount = subscribers.filter((s) => s.lang === 'nl' && !s.unsubscribed_at).length;
  const enCount = subscribers.filter((s) => s.lang === 'en' && !s.unsubscribed_at).length;

  return (
    <Tabs defaultValue="artworks" className="max-w-5xl mx-auto space-y-8">
      <TabsList>
        <TabsTrigger value="artworks">Artworks</TabsTrigger>
        <TabsTrigger value="subscribers">Newsletter ({subscribers.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="artworks" className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>{draft.id ? 'Edit artwork' : 'Add new artwork'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Title (NL)</Label>
                <Input
                  value={draft.title_nl ?? ''}
                  onChange={(e) =>
                    set({
                      title_nl: e.target.value,
                      slug: draft.id ? draft.slug : slugify(e.target.value),
                    })
                  }
                />
              </div>
              <div>
                <Label>Title (EN)</Label>
                <Input
                  value={draft.title_en ?? ''}
                  onChange={(e) => set({ title_en: e.target.value })}
                />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input value={draft.slug} onChange={(e) => set({ slug: slugify(e.target.value) })} />
              </div>
              <div>
                <Label>Year</Label>
                <Input
                  type="number"
                  value={draft.year ?? ''}
                  onChange={(e) => set({ year: e.target.value ? Number(e.target.value) : null })}
                />
              </div>
              <div>
                <Label>Materials (NL)</Label>
                <Input
                  value={draft.materials_nl ?? ''}
                  onChange={(e) => set({ materials_nl: e.target.value })}
                />
              </div>
              <div>
                <Label>Materials (EN)</Label>
                <Input
                  value={draft.materials_en ?? ''}
                  onChange={(e) => set({ materials_en: e.target.value })}
                />
              </div>
              <div>
                <Label>Dimensions</Label>
                <Input
                  value={draft.dimensions ?? ''}
                  onChange={(e) => set({ dimensions: e.target.value })}
                />
              </div>
              <div>
                <Label>Price (in euros, leave empty for price on request)</Label>
                <Input
                  type="number"
                  value={draft.price_cents != null ? draft.price_cents / 100 : ''}
                  onChange={(e) =>
                    set({ price_cents: e.target.value ? Math.round(Number(e.target.value) * 100) : null })
                  }
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Description (NL)</Label>
                <Textarea
                  rows={6}
                  value={draft.description_nl ?? ''}
                  onChange={(e) => set({ description_nl: e.target.value })}
                />
              </div>
              <div>
                <Label>Description (EN)</Label>
                <Textarea
                  rows={6}
                  value={draft.description_en ?? ''}
                  onChange={(e) => set({ description_en: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Images (square works best)</Label>
              <Input
                type="file"
                accept="image/*"
                multiple
                disabled={uploading}
                onChange={(e) => upload(e.target.files)}
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {(draft.images ?? []).map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img.url} alt="" className="h-20 w-20 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() =>
                        set({ images: (draft.images ?? []).filter((_, idx) => idx !== i) })
                      }
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-black text-white text-xs"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!draft.sold}
                  onChange={(e) => set({ sold: e.target.checked })}
                />
                Sold
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.published !== false}
                  onChange={(e) => set({ published: e.target.checked })}
                />
                Published
              </label>
              <div className="flex items-center gap-2 text-sm">
                Sort order
                <Input
                  type="number"
                  className="w-20"
                  value={draft.sort_order ?? 0}
                  onChange={(e) => set({ sort_order: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={save} disabled={saving || uploading}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {draft.id && (
                <Button variant="outline" onClick={() => setDraft(emptyDraft)}>
                  Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Artworks ({artworks.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <div className="divide-y">
                {artworks.map((a) => (
                  <div key={a.id} className="flex items-center gap-4 py-3">
                    {a.images[0]?.url && (
                      <img src={a.images[0].url} alt="" className="h-12 w-12 object-cover rounded" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-medium">{a.title_nl}</div>
                      <div className="text-xs text-muted-foreground">
                        /{a.slug} · {a.published ? 'published' : 'draft'}
                        {a.sold ? ' · sold' : ''}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => edit(a)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(a)}>
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="subscribers">
        <Card>
          <CardHeader>
            <CardTitle>Newsletter subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-2xl font-semibold">{subscribers.length}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
              </div>
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-2xl font-semibold">{nlCount}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">NL</div>
              </div>
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-2xl font-semibold">{enCount}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">EN</div>
              </div>
            </div>
            {subscribersLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <div className="divide-y">
                {subscribers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-sm font-medium">{s.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.lang.toUpperCase()} · {s.source} ·{' '}
                        {new Date(s.subscribed_at).toLocaleDateString('nl-NL')}
                        {s.unsubscribed_at && ' · unsubscribed'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
